/* eslint-disable react-refresh/only-export-components */
import { createContext, ReactNode, useEffect, useState } from "react";
import { getCurrentUserInfo } from "../lib/api";
import { initializeFirebase, onAuthChanged, signOut as firebaseSignOut } from "../lib/firebase";
import { useInstrumentation } from "../hooks/useInstrumentation";
import type { FeatureFlags } from "../lib/features";

export interface UserProfile {
  displayName: string;
  profileUrl: string | null;
}

export interface User {
  id: string;
  email: string;
  createdAt: string;
  lastLoginAt: string | null;
  profile: UserProfile | null;
}

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  features: FeatureFlags;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [features, setFeatures] = useState<FeatureFlags>({});
  const [loading, setLoading] = useState(true);
  const instrumentation = useInstrumentation();

  useEffect(() => {
    initializeFirebase();

    const unsubscribe = onAuthChanged(async (firebaseUser) => {
      if (firebaseUser) {
        setLoading(true);
        try {
          const [dbUser, tokenResult] = await Promise.all([getCurrentUserInfo(), firebaseUser.getIdTokenResult()]);
          setUser(dbUser);
          setFeatures((tokenResult.claims.features as FeatureFlags) ?? {});
          instrumentation.identify(dbUser.id);
        } catch {
          setUser(null);
          setFeatures({});
        }
      } else {
        setUser(null);
        setFeatures({});
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [instrumentation]);

  const signOut = async () => {
    instrumentation.reset();
    await firebaseSignOut();
    setUser(null);
    setFeatures({});
  };

  return <AuthContext.Provider value={{ user, loading, signOut, features }}>{children}</AuthContext.Provider>;
}
